// @flow
import React, { useContext, useState } from 'react'
import styled from 'styled-components'
import Select from 'react-select'
import { useQuery } from 'react-query'

import { actions, DesignContext } from '../store'
import { colors } from '../styles'
import logo from '../assets/img/empire-logo.png'
import { getSampleGroups } from '../api'
import SampleGroup from './SampleGroup'

type Props = {
  designId: string | number,
  onAddItem: Function,
  onSave: Function,
  onClone: Function,
  onExportPDF: Function,
  onChangeBoard: Function,
  isCollection: boolean,
}

const trimPrefix = (string) => {
  if (string.indexOf('.') > 0) {
    return string.slice(string.indexOf('.') + 1)
  }
  return string
}

const Sidebar = ({ designId, onAddItem, onSave, onClone, onChangeBoard, onExportPDF, isCollection }: Props): Function => {
  const { state, dispatch } = useContext(DesignContext)
  const { config: { communities }, community, lot, boards, currentBoardIndex, externalLabel } = state
  const board = boards[ currentBoardIndex ]
  const { data: sampleGroups } = useQuery('sampleGroups', getSampleGroups)
  const [ helpOpen, setHelpOpen ] = useState(false)
  // console.log('sidebar data', designId)

  let lotLabel = lot?.label
  const getLabel = (option) => option.title
  const getValue = (option) => option.id
  const setCommunity = (value) => dispatch({ type: actions.SET_COMMUNITY, community: value })
  const updateLot = (evt) => dispatch({ type: actions.UPDATE_LOT, lot: { [ trimPrefix(evt.target.name) ]: evt.target.value } })
  const updateLabel = (evt) => dispatch({ type: actions.UPDATE_LABEL, externalLabel: evt.target.value })
  const updateBoard = (evt) => dispatch({ type: actions.UPDATE_BOARD, board: { [ trimPrefix(evt.target.name) ]: evt.target.value } })
  const resetBoard = null // (evt) => dispatch({ type: actions.RESET_BOARD, board: { board } })
  const changeBoard = (evt) => onChangeBoard(evt.target.dataset.board)
  const saveBoard = (evt) => {
    console.log('saving...')
    onSave()
  }
  const printBoard = (evt) => {
    console.log('printing...')
    window.print()
  }
  const cloneBoard = () => {
    console.log('cloning...')
    onClone()
  }
  const exportBoard = () => {
    onExportPDF && onExportPDF()
  }
  const addText = (evt,) => {
    onAddItem(evt, { label: 'Add text here', attributes: { width: 100, height: 32 } })
  }
  const readMore = () => setHelpOpen(!helpOpen)

  return (
    <Panel>
      <Id>
        ID: {(designId && designId !== 'null') ? designId : 'N/A'}
      </Id>
      <header className="header"><Logo src={logo} alt="Empire Communities logo" /></header>
      <Rule />
      <div>
        <Field>
          <Select options={communities} value={community} placeholder="Select Community" getOptionLabel={getLabel}
                  getOptionValue={getValue} onChange={setCommunity} styles={SelectFieldStyles} theme={SelectFieldTheme} />
        </Field>
      </div>
      <div>
        <Field>
          {isCollection ? (
            <label>Label
              <TextField type="text" name="externalLabel" placeholder="Label" value={externalLabel} onChange={updateLabel} />
            </label>
          ) : (
          <label>Lot
            <TextField type="text" name="lot.label" placeholder="Lot Label" value={lotLabel} onChange={updateLot} />
          </label>
          )}
        </Field>
        <Field>
          <label htmlFor="boardLabel">Board</label>
          <TabContainer>
            <Tabs onClick={changeBoard} data-current-board={currentBoardIndex}>
              {boards.map((board, i) => (
                <Tab key={`tab-${i}`} data-board={i}>{i + 1}. {board.label}</Tab>
              ))}
            </Tabs>
            <TextField type="text" id="boardLabel" name="board.label" placeholder="Board Label" value={board?.label} onChange={updateBoard} />
            {currentBoardIndex === 2 && (
              <TextField type="text" id="boardLabel2" name="board.label2" placeholder="Board Label #2"
                         value={board?.label2} onChange={updateBoard} style={{ marginTop: 5 }} />
            )}
          </TabContainer>
        </Field>
      </div>
      <Rule />
      <div>
        <SectionTitle>Samples</SectionTitle>
        {sampleGroups && sampleGroups.groups.map(group => (
          <Field key={`group-${group.id}`}>
            <SampleGroup onAddItem={onAddItem} {...group} />
          </Field>
        ))}
        <Field>
          <ActionButton onClick={addText}>Add Text</ActionButton>
        </Field>
      </div>
      <Rule />
      <SectionTitle>Actions</SectionTitle>
      {resetBoard && <ActionButton onClick={resetBoard}>Reset</ActionButton>}
      {onSave && <ActionButton onClick={saveBoard} title="Save to server">Save</ActionButton>}
      {onClone && <ActionButton onClick={cloneBoard} title="Make a copy of this board">Clone</ActionButton>}
      <ActionButton onClick={printBoard}>Print</ActionButton>
      {onExportPDF && <ActionButton onClick={exportBoard}>Get PDF</ActionButton>}
      {onExportPDF && <SmallType>Please note that "Get PDF" is not yet finished. Using Print + Save to PDF via your browser will work better at the
        moment. </SmallType>}
      <Rule />
      <SectionTitle>Help</SectionTitle>
      <HelpText className={helpOpen ? '--open' : '--closed'}>
        <ReadMoreBackground />
        <ReadMore onClick={readMore}>Read {helpOpen ? 'Less' : 'More'}</ReadMore>
        <Para>Choose a community and enter the Lot & Board labels. For Board #3, entering a second label will add a vertical divider line; you can
          move this line left & right as needed.
        </Para>
        <Para>
          Selecting a sample from the menus above will place the image & label in the middle of the board. To fill in a specific location,
          first select the placeholder spot, then select the sample, which will fit the sample image to the placeholder, and add the associated label
          if applicable. To add a sample to any part of the board, simply double-click on the empty space, then select
          a sample; the image will be dropped onto that spot, with its label directly below.
        </Para>
      </HelpText>
    </Panel>
  )
}

export default Sidebar

const Panel = styled.div`
  position: relative;
  padding: 1rem 1rem 4rem;
`

const SelectFieldStyles = {
  control: (provided, state) => ({
    ...provided,
    padding: 0,
    margin: 0,
    minHeight: 26,
    fontSize: 14,
    color: colors.midLtGray
  }),
  singleValue: (provided) => ({ ...provided, padding: 0, margin: 0, top: '65%' }),
  input: (provided) => ({ ...provided, padding: 0, margin: 0 }),
  valueContainer: (provided) => ({ ...provided, paddingLeft: 4 }),
  indicatorsContainer: (provided) => ({ ...provided, padding: 0 }),
  indicatorContainer: (provided) => ({ ...provided, padding: 0, color: colors.midGray }),
  dropdownIndicator: (provided) => ({ ...provided, padding: 0, paddingRight: 2, color: colors.midGray, width: 18, height: 18 }),
  indicatorSeparator: (provided) => ({ width: 0 })
}

const SelectFieldTheme = (theme) => ({
  ...theme,
  borderRadius: 0,
  colors: {
    ...theme.colors,
    primary: colors.midGray,
    primary25: colors.lightGray
  }
})

const HelpText = styled.div`
  position: relative;
  max-height: 90px;
  overflow: hidden;
  transition: all 0.5s ease-in-out;
  padding-bottom: 1rem;
  color: ${colors.midGray};
  
  &.--open {
    max-height: 1000rem;
    transition: all 0.5s ease-in-out;
  }
`

const ReadMoreBackground = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 10px;
  border-top: 1px solid ${colors.midGray};
  background:  ${colors.lightGray};

`

const ReadMore = styled.button`
  position: absolute;
  bottom: 0;
  right: 1rem;
  display: inline-block;
  font-size: 14px;
  color: ${colors.midDkGray};
  line-height: 1.0;
  padding: 0.25rem;
  height: 20px;
  border: 1px solid ${colors.midLtGray};
  background:  ${colors.lightGray};
`

const Para = styled.p`
  margin: 0.25rem 0 0.5rem;
  color: ${colors.midDkGray};
`

const Id = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  color: ${colors.darkGray};
  font-weight: bold;
  font-size: 14px;
`

const Logo = styled.img`
  width: 100%;
  max-width: 150px;
`

const Rule = styled.hr`
  background: ${colors.midGray};
  border: 1px solid ${colors.midGray};
`

const Field = styled.div`
  margin-bottom: 0.5rem;
  
  label {
    color: ${colors.darkGray};
  }
`

const SectionTitle = styled.h3`
  color: ${colors.midLtGray};
  text-transform: uppercase;
  font-size: 16px;
  margin: 0;
`

const TextField = styled.input`
  width: 100%;
  color: ${colors.darkGray};
  border: 1px solid ${colors.midGray};
`

const SmallType = styled.p`
  font-size: 12px;
  color: ${colors.darkGray};
`

const TabContainer = styled.div`
  //border: 1px solid ${colors.midGray};
`

const Tab = styled.div`
  width: 33.33%;
  font-size: 12px;
  white-space: nowrap;
  border: 1px solid ${colors.midGray};
  padding: 0.25rem 0.2rem 0;
  text-overflow: ellipsis;
  overflow: hidden;
  
  &:not(:first-child) {
    border-left-color: transparent;
  }
  
  
  &.active {
    border-bottom-color: transparent;
  }
`

const Tabs = styled.div`
  display: flex;
  flex-wrap: nowrap; 
  justify-content: stretch;
  width: 100%;
  margin-bottom: 0.5rem;
  
  &[data-current-board="0"] ${Tab}:nth-child(1),
  &[data-current-board="1"] ${Tab}:nth-child(2),
  &[data-current-board="2"] ${Tab}:nth-child(3) {
     //border-bottom-width: 3px;
     background-color: ${colors.midGray};
     color: #fff;
  }
`

const ActionButton = styled.button`
  margin: 0 0.5rem 0.5rem 0;
  color: ${colors.midDkGray};
  border: 1px solid ${colors.midGray};
`